import { useEffect, useState } from "react"

import { Alert, App, Col, Divider, Flex, Modal, Row, Space, Switch, Typography } from "antd"
import PropTypes from "prop-types"
import { useTranslation } from "react-i18next"
import { connect } from "react-redux"
import { useLocation, useNavigate, useSearchParams } from "react-router-dom"

import ButtonComponent from "../../../Components/Button"
import InfoTooltip from "../../../Components/InfoTooltip/InfoTooltip"
import InputComponent from "../../../Components/Input"
import InputNumberComponent from "../../../Components/Input/InputNumber"
import LogsModal from "../../../Components/Modal/LogsModal/LogsModal"
import { INCOGNITO_MASK } from "../../../Components/Private"
import { SupportedPlatforms } from "../../../Components/SupportedPlatforms"
import withPermission from "../../../hoc/withPermission"
import { useExternalDemoMode } from "../../../hooks/useExternalDemoMode"
import { useIncognito } from "../../../hooks/useIncognito"
import Icon from "../../../Icon"
import { modalConfig } from "../../../theme"
import { extendedAccessRoles, highAccessRoles } from "../../../Utils/permission"
import { buildSearchWithFlag, hasQueryFlag } from "../../../Utils/queryFlags"
import { getBase64, validateUrl } from "../../../Utils/utils"
import TitleSection from "../../BusinessSettings/components/TitleSection"
import { authErrorReset } from "../../LoginPage/redux/auth.actions"
import { userActions } from "../../User/redux/user.slice"
import {
  getAutoRefundLogs,
  getBusinessAccount,
  getBusinessAccountsWithRole,
  getPaymentProcessors,
  updateBusinessAccount
} from "../../User/redux/user.thunks"
import ChangeRefundForm from "./ChangeRefundForm"

const { Text, Link } = Typography

const description = {
  other: "Threshold changes for Ethoca and CDRN are instant.",
  rdr: "It can take up to five days for RDR rule changes to become active.",
  auto_cancel_amount:
    "Enable automatic subscription cancellations and set a maximum amount for auto-cancellation."
}

// const autoRefundSupportedList = ['stripe', 'braintree', 'shopify', 'chargebee', 'recurly','authorize.net', 'airwallex', 'next', 'nmi',
//   // 'adyen',
// ]
const autoCancelSupportedList = ["stripe"]
const AUTO_REFUND_LOGS_QUERY_PARAM = "auto-refund-logs"

const PermissionButtonHigh = withPermission(ButtonComponent, highAccessRoles)
const PermissionInputHigh = withPermission(InputComponent, highAccessRoles)

const PermissionButtonExtended = withPermission(ButtonComponent, extendedAccessRoles)
const PermissionSwitchExtended = withPermission(Switch, extendedAccessRoles)

const AutoRefundPage = ({
  saveFetching,
  isDemo,
  isAdmin,
  currentBusinessAccountId,
  ba_role,

  //businessAccountFetching,
  businessAccount,
  paymentProcessors,

  saveResult,
  nav,
  error,
  userError,
  autoRefundLogs,
  ...props
}) => {
  const { isMobile, theme } = nav
  const { pathname, search } = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const isExternalDemoMode = useExternalDemoMode()
  const incognito = useIncognito()

  const autoRefundSupportedList = paymentProcessors
    ?.filter(
      (item) =>
        ![
          "manual_refunds",
          "slack",
          "recharge",
          "zapier",
          "claude",
          "chatgpt",
          "custom_mcp"
        ].includes(item?.service_name)
    )
    ?.map((item) => item?.service_name)

  const businessAccountId = isAdmin
    ? searchParams.get("business_account_id")
    : currentBusinessAccountId

  const isLogsModalOpen = hasQueryFlag(search, AUTO_REFUND_LOGS_QUERY_PARAM)
  const hasAdyen = businessAccount?.connected_payment_processors?.includes("stripe")

  const { name, url, photo } = businessAccount || {
    name: null,
    url: null,
    photo: null,
    is_disabled_auto_cancel: false,
    enable_auto_cancel: false,
    auto_cancel_amount: null
  }

  const {
    id,
    is_use_auto_refund_other,
    max_sum_for_auto_refund_other,
    is_use_auto_refund_rdr,
    max_sum_for_auto_refund_rdr
  } = businessAccount?.settings_for_auto_refund || {
    id: null,
    is_use_auto_refund_rdr: false,
    max_sum_for_auto_refund_rdr: null,
    is_use_auto_refund_other: false,
    max_sum_for_auto_refund_other: null
  }

  const {
    id: subscriptionId,
    is_disabled_auto_cancel,
    enable_auto_cancel,
    auto_cancel_amount
  } = businessAccount?.["subscription_settings"]?.[0] || {
    id: null,
    is_disabled_auto_cancel: false,
    enable_auto_cancel: false,
    auto_cancel_amount: null
  }

  const isPaymentProcessorConnected = !!businessAccount?.connected_payment_processors?.filter(
    (item) => item !== "manual_refunds"
  ).length

  const initialAmountMap = {
    other: max_sum_for_auto_refund_other || 0,
    rdr: max_sum_for_auto_refund_rdr || 0,
    auto_cancel_amount: auto_cancel_amount || 0
  }

  const [loading, setLoading] = useState(false)

  const { t } = useTranslation()
  const { notification, modal } = App.useApp()

  const [data, setData] = useState({ name, url })

  const [field, setField] = useState("")

  const [avatarError, setAvatarError] = useState("")
  const [urlError, setUrlError] = useState(false)
  const [visible, setVisible] = useState(null) // rdr or other or auto_cancel_amount

  const isDemoModeBlocked = currentBusinessAccountId && isDemo
  const businessAccountRole = ba_role

  const getInputText = (value, isEnabled, type) => {
    const enabledTextMap = {
      auto_refund: "All transactions are refunded",
      auto_cancel: "Cancel all subscriptions"
    }
    if (value === 0 && isEnabled) {
      return enabledTextMap[type]
    }
    // Once a threshold is set the placeholder spells out a price, so it is hidden in Incognito mode.
    // "All transactions are refunded" and a plain 0 carry no amount and stay readable (CHA1-1354).
    if (incognito && Number(value) !== 0) {
      return INCOGNITO_MASK
    }
    if (value !== 0 && isEnabled) {
      return `0.00 - ${Number(value).toFixed(2)}`
    }
    return value
  }

  const getAutoRefundText = (value, isEnabled) => {
    return getInputText(value, isEnabled, "auto_refund")
  }

  const getAutoCancelText = (value, isEnabled) => {
    return getInputText(value, isEnabled, "auto_cancel")
  }

  const changedField = {
    photo: t("Logotype"),
    name: t("Business account name"),
    url: t("Business URL"),
    max_sum_for_auto_refund: t("Auto-refund amount"),
    auto_cancel_amount: t("Auto-cancel amount")
  }

  const notificationConfig = {
    closeIcon: <Text>{t("Dismiss")}</Text>,
    style: { minWidth: "max-content" }
  }

  const notificationSuccessText = (field) => {
    return {
      message: t("_field_ was replaced successfully", { field: changedField[field] })
    }
  }

  const updateAutoRefundSettings = (payload) => {
    props.updateBusinessAccount({
      ...payload,
      skipExtraAction: true
    })
  }

  useEffect(() => {
    if (businessAccount?.id) setData({ name, url, is_use_auto_refund_other, enable_auto_cancel })
  }, [businessAccount?.id])

  useEffect(() => {
    if (!paymentProcessors?.length) {
      props.getPaymentProcessors({
        integrationsPage: true,
        merchant_id: businessAccountId,
        return_url: process.env.REACT_APP_BASE_URI + pathname
      })
    }
  }, [paymentProcessors?.length])

  useEffect(() => {
    if (id) {
      props.getAutoRefundLogs(id)
    }
  }, [id])

  useEffect(() => {
    if (error?.message) notification.error({ message: error?.message?.detail })
    if (error) props.resetError()
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [error])

  useEffect(() => {
    if (saveResult && !saveResult?.message) {
      props.getBusinessAccountsWithRole()
      if (id) {
        props.getAutoRefundLogs(id)
      }
      if (field) {
        let message = ""
        if (field === "photo") {
          if (photo) {
            message = notificationSuccessText(field)
          } else {
            message = {
              message: t("_field_ was removed successfully", { field: changedField[field] })
            }
          }
        } else if (field === "is_use_auto_refund") {
          if (is_use_auto_refund_other) {
            message = { message: t("Auto-refund was turned on successfully") }
          } else {
            message = { message: t("Auto-refund was turned off successfully") }
          }
        } else if (field === "enable_auto_cancel") {
          if (enable_auto_cancel) {
            message = { message: t("Auto-cancel was turned on successfully") }
          } else {
            message = { message: t("Auto-cancel was turned off successfully") }
          }
        } else {
          message = notificationSuccessText(field)
        }
        notification.success({
          ...message,
          ...notificationConfig
        })
        setField("")
        if (visible) setVisible(null)
        props.userSaveResultReset()
      }
    }
    if (userError) {
      if (userError?.confirmError?.detail) {
        notification.error({
          message: userError?.confirmError?.detail,
          ...notificationConfig
        })
        props.userErrorsReset()
      } else if (userError?.businessAccountError) {
        // notification.error({
        //   message: userError?.businessAccountError || t('Something went wrong, kindly try again'),
        //   ...notificationConfig,
        // })
        // props.userErrorsReset()
      }
    }
    /*eslint-disable-next-line react-hooks/exhaustive-deps*/
  }, [saveResult, userError, field])

  const fakeRequest = ({ onSuccess }) => {
    setTimeout(() => {
      onSuccess("ok")
    }, 500)
  }

  const beforeUpload = (file) => {
    const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png"
    if (!isJpgOrPng) {
      setAvatarError(
        "You can upload file only in JPG/ PNG formats, please select a different image."
      )
      setLoading(false)
      return false
    }
    const isLt10M = file.size / 1024 / 1024 < 10
    if (!isLt10M) {
      setAvatarError(
        "Image upload failed: your image is bigger than 10MB, please select a different image."
      )
      setLoading(false)
      return false
    }
    if (avatarError) setAvatarError("")
    return true
  }

  const handleChangeImage = (info = null) => {
    if (info === null) {
      if (avatarError) setAvatarError("")
      setLoading(false)
      setField("photo")
      props.updateBusinessAccount({
        id: businessAccountId,
        photo: null
      })
      return
    }
    if (info.file.status === "uploading") {
      setLoading(true)
      return
    }
    if (info.file.status === "done") {
      getBase64(info.file.originFileObj, () => {
        setLoading(false)
        setField("photo")
        props.updateBusinessAccount({
          id: businessAccountId,
          photo: info.file.originFileObj
        })
      })
    }
  }

  const handleUpdateUserInfo = (params, value, type) => {
    if (params === "is_use_auto_refund") {
      if (!value) {
        modal.confirm({
          ...modalConfig("exclamation"),
          centered: !isMobile,

          title: t("Disable auto-refund"),
          content: t(
            "Are you sure you want to disable auto-refunds? If you proceed, you will be required to manually manage refunds of all incoming alerts."
          ),

          cancelText: t("Go back"),
          okText: t("Disable"),
          onOk() {
            let newData = {
              id: id,
              business_account_settings: true,
              [`${params}${type ? `${type}` : ""}`]: value,
              [`max_sum_for_auto_refund${type ? `${type}` : ""}`]: 0
            }
            updateAutoRefundSettings(newData)
          }
        })
      } else {
        let newData = {
          id: id,
          business_account_settings: true,
          [`${params}${type ? `${type}` : ""}`]: value
        }
        updateAutoRefundSettings(newData)
      }
    } else if (params === "enable_auto_cancel") {
      let newData = {
        id: businessAccountId,
        subscription_settings: [
          {
            id: subscriptionId,
            [params]: value,
            ...(!value && { auto_cancel_amount: 0 })
          }
        ]
      }
      updateAutoRefundSettings(newData)
    } else {
      if (businessAccount[params] !== data[params]) {
        let newValue = data[params]?.trim()
        if (newValue) {
          if (params === "url" && !validateUrl.test(newValue)) {
            setUrlError(true)
            return
          }
          props.updateBusinessAccount({
            id: params === "max_sum_for_auto_refund" ? id : businessAccountId,
            ...(params === "max_sum_for_auto_refund" && { business_account_settings: true }),
            [params]: newValue
          })
        } else {
          let oldValue = null
          if (params === "name") oldValue = name
          if (params === "url") oldValue = url
          if (oldValue) setData((prev) => ({ ...prev, [params]: oldValue }))
        }
      } else {
        return
      }
    }

    changePersonalData(params)
  }

  function changePersonalData(params) {
    setField(params)
  }

  function handleChange(e) {
    const { name, value } = e.target
    setData((prev) => ({ ...prev, [name]: value }))
  }

  const handleSubmitAmount = (amount) => {
    if (Number(amount) === Number(initialAmountMap[visible])) {
      setVisible(null)
    } else {
      let key = visible === "auto_cancel_amount" ? visible : `max_sum_for_auto_refund_${visible}`
      let personalDataField = visible === "auto_cancel_amount" ? visible : "max_sum_for_auto_refund"

      let data = null
      if (visible === "auto_cancel_amount") {
        data = {
          id: businessAccountId,
          subscription_settings: [
            {
              id: subscriptionId,
              [key]: amount || 0
            }
          ]
        }
      } else {
        data = {
          id,
          business_account_settings: true,
          [key]: amount || 0
        }
      }
      updateAutoRefundSettings(data)
      changePersonalData(personalDataField)
    }
  }

  const title = {
    rdr: "Auto-refund setup",
    other: "Auto-refund setup",
    auto_cancel_amount: "Auto-cancel setup"
  }

  const handleCloseLogsModal = () => {
    navigate({
      pathname,
      search: buildSearchWithFlag(search, AUTO_REFUND_LOGS_QUERY_PARAM, false)
    })
  }

  return (
    <div style={{ padding: !isAdmin && !isMobile ? 32 : !isAdmin && isMobile ? 16 : "0 0 20px 0" }}>
      {isAdmin && (
        <TitleSection
          title={`Auto-refund configure for ${businessAccount?.name}`}
          link={businessAccount?.url}
          queryParam={AUTO_REFUND_LOGS_QUERY_PARAM}
          queryButtonText="View logs"
        />
      )}
      {isMobile && (
        <Flex gap={4} vertical style={{ paddingBottom: isMobile && 24 }}>
          <Text strong>{t("Auto-refund")}</Text>
          <Text type="secondary">{t("Keep this turned on if you plan to refund all alerts.")}</Text>
        </Flex>
      )}
      <Row style={{ flexDirection: isMobile && "column-reverse" }}>
        <Col flex={!isMobile && "1 1 300px"}>
          <Flex vertical gap={24} style={{ maxWidth: 500 }}>
            {!isMobile && (
              <Flex gap={4} vertical>
                <Text strong>{t("Auto-refund")}</Text>
                <Text type="secondary">
                  {t("Keep this turned on if you plan to refund all alerts.")}
                </Text>
              </Flex>
            )}
            <Flex gap={16} vertical>
              <Alert
                type={"warning"}
                icon={<Icon type={"alert_triangle_error"} style={{ fontSize: 20 }} />}
                showIcon
                message={<Text strong>{t("Pay attention")}</Text>}
                description={
                  <Text>You still need to pay for alerts that exceed your refund rules.</Text>
                }
              />
              <SupportedPlatforms list={autoRefundSupportedList} />
            </Flex>
          </Flex>
        </Col>

        <Col flex={!isMobile ? "0 1 420px" : "unset"} style={{ paddingBottom: isMobile && 12 }}>
          <Flex vertical>
            <Flex gap={12} align={"start"} vertical>
              <Flex gap={12}>
                <PermissionSwitchExtended
                  defaultChecked={is_use_auto_refund_other}
                  checked={is_use_auto_refund_other}
                  disabled={
                    !isPaymentProcessorConnected || !businessAccount?.id || isExternalDemoMode
                  }
                  onChange={(checked) =>
                    handleUpdateUserInfo("is_use_auto_refund", checked, "_other")
                  }
                  role={isAdmin ? 1 : businessAccountRole}
                />
                <Text>Ethoca & CDRN</Text>
              </Flex>
              <Flex gap={16} vertical style={{ width: "100%" }}>
                <Flex gap={8} vertical>
                  <Flex gap={12}>
                    <InputNumberComponent
                      key={is_use_auto_refund_other ? 1 : 0}
                      addonBefore={<Text type="secondary">$</Text>}
                      value={
                        getAutoRefundText(
                          max_sum_for_auto_refund_other,
                          is_use_auto_refund_other
                        ) || 0
                      }
                      disabled={true}
                      min={0}
                      step={10}
                      precision={2}
                      style={{ width: "100%" }}
                    />
                    {is_use_auto_refund_other && (
                      <PermissionButtonExtended
                        size="large"
                        onClick={() => setVisible("other")}
                        role={isAdmin ? 1 : businessAccountRole}
                      >
                        {t("Edit")}
                      </PermissionButtonExtended>
                    )}
                  </Flex>
                  {is_use_auto_refund_other && (
                    <Text type="secondary">To disable auto-refund, turn off the toggle above.</Text>
                  )}
                </Flex>
              </Flex>
            </Flex>
            <Divider />
            <Flex gap={12} vertical>
              <Flex gap={12}>
                <PermissionSwitchExtended
                  defaultChecked={is_use_auto_refund_rdr}
                  checked={is_use_auto_refund_rdr}
                  disabled={true}
                  onChange={(checked) =>
                    handleUpdateUserInfo("is_use_auto_refund", checked, "_rdr")
                  }
                  role={isAdmin ? 1 : businessAccountRole}
                />
                <Flex gap={4} align="center">
                  <Text>RDR</Text>
                  <InfoTooltip
                    placement="topLeft"
                    title={
                      <div style={{ textAlign: "left" }}>
                        RDR only supports automatic refunds. If you need more advanced rules for
                        handling RDR alerts, please <br />
                        <Link
                          href="https://help.chargeback.io/en/articles/12782873-adding-more-rules-to-rdr"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          read this article
                        </Link>
                      </div>
                    }
                  />
                </Flex>
              </Flex>

              <Flex vertical gap={16} style={{ width: "100%" }}>
                <Flex gap={12}>
                  <InputNumberComponent
                    key={is_use_auto_refund_rdr ? 1 : 0}
                    addonBefore={<Text type="secondary">$</Text>}
                    value={
                      getAutoRefundText(max_sum_for_auto_refund_rdr, is_use_auto_refund_rdr) || 0
                    }
                    disabled={true}
                    min={0}
                    step={10}
                    precision={2}
                    style={{ width: "100%" }}
                  />
                  {is_use_auto_refund_rdr && (
                    <PermissionButtonExtended
                      size="large"
                      onClick={() => setVisible("rdr")}
                      role={isAdmin ? 1 : businessAccountRole}
                    >
                      {t("Edit")}
                    </PermissionButtonExtended>
                  )}
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Col>
      </Row>

      <Divider />
      {isMobile && (
        <Flex vertical gap={8} style={{ paddingBottom: 24 }}>
          <Text strong>{t("Auto-cancel subscriptions")}</Text>
          <Text type="secondary" style={{ maxWidth: 524 }}>
            {t(
              "Automatically cancel subscriptions linked to alerts. That way we prevent future chargebacks from that customer."
            )}
          </Text>
        </Flex>
      )}
      <Row style={{ flexDirection: isMobile && "column-reverse", paddingBottom: isMobile && 100 }}>
        <Col flex={!isMobile && "1 1 300px"}>
          <Flex gap={8} vertical style={{ maxWidth: 500 }}>
            {!isMobile && (
              <>
                <Text strong>{t("Auto-cancel subscriptions")}</Text>
                <Text type="secondary" style={{ maxWidth: 524 }}>
                  {t(
                    "Automatically cancel subscriptions linked to alerts. That way we prevent future chargebacks from that customer."
                  )}
                </Text>
              </>
            )}

            <SupportedPlatforms list={autoCancelSupportedList} style={{ marginTop: 24 }} />
          </Flex>
        </Col>
        <Col flex={!isMobile ? "0 1 420px" : "unset"}>
          <Flex vertical gap={32}>
            <Flex gap={12} vertical>
              <Flex gap={12}>
                <PermissionSwitchExtended
                  defaultChecked={enable_auto_cancel}
                  checked={enable_auto_cancel}
                  disabled={is_disabled_auto_cancel || !businessAccount?.id || isExternalDemoMode}
                  onChange={(checked) => handleUpdateUserInfo("enable_auto_cancel", checked)}
                  role={isAdmin ? 1 : businessAccountRole}
                />
                <Flex gap={4} align="center">
                  <Text>{t("Enable auto-cancel")}</Text>
                  <InfoTooltip
                    placement="topLeft"
                    title={t(
                      "Enabling Auto-cancel will automatically unsubscribe users from their subscription plans for auto-refunded transactions."
                    )}
                  />
                </Flex>
              </Flex>

              <Space direction="vertical" size={16} style={{ flexGrow: 1 }}>
                <Flex gap={12}>
                  <InputNumberComponent
                    key={enable_auto_cancel ? 1 : 0}
                    addonBefore={<Text type="secondary">$</Text>}
                    value={getAutoCancelText(auto_cancel_amount, enable_auto_cancel) || 0}
                    disabled={true}
                    min={0}
                    step={10}
                    precision={2}
                    style={{ width: "100%" }}
                  />
                  {enable_auto_cancel && (
                    <PermissionButtonExtended
                      size="large"
                      onClick={() => setVisible("auto_cancel_amount")}
                      role={isAdmin ? 1 : businessAccountRole}
                      disabled={!businessAccount?.id || isDemoModeBlocked}
                    >
                      {t("Edit")}
                    </PermissionButtonExtended>
                  )}
                </Flex>
              </Space>
            </Flex>
          </Flex>
        </Col>
      </Row>
      <LogsModal
        title={"Logs"}
        description={"View the latest activity for auto-refund."}
        logs={autoRefundLogs}
        open={isLogsModalOpen}
        onClose={handleCloseLogsModal}
      />
      <Modal
        classNames={{ content: "clear-bg" }}
        {...(isMobile ? { transitionName: "" } : null)}
        getContainer={() => document.getElementById("main-layout")}
        open={!!visible}
        title={visible ? t(title[visible]) : ""}
        centered={!isMobile}
        footer={null}
        closable="true"
        onCancel={() => setVisible(false)}
        destroyOnClose
        width={442}
        closeIcon={<Icon type="cross_small" />}
        className="change-refund-form-modal"
      >
        <Text type="secondary">{t(description[visible])}</Text>

        <ChangeRefundForm
          onSubmit={handleSubmitAmount}
          onClose={() => setVisible(false)}
          initialAmount={initialAmountMap[visible] || 0}
          theme={theme}
          type={visible}
          submitDisabled={isExternalDemoMode}
          isMobile={isMobile}
        />
      </Modal>
    </div>
  )
}

AutoRefundPage.propTypes = {
  fetching: PropTypes.bool,
  businessAccount: PropTypes.object
}

const mapStateToProps = (state) => ({
  nav: state.nav,
  currentBusinessAccountId: state.auth.userInfo.current_business_account_id,
  ba_role: state.auth.userInfo?.ba_role?.id,
  isDemo: state.auth.userInfo.is_demo,
  isAdmin: state.auth.isAdmin,
  //businessAccountFetching: state.user.businessAccountFetching,
  businessAccount: state.user.businessAccount,

  //businessAccounts: state.user.businessAccounts,

  saveFetching: state.user.saveFetching,
  paymentProcessors: state.user.paymentProcessors,
  autoRefundLogs: state.user.autoRefundLogs,

  saveResult: state.user.saveResult,
  error: state.auth.error,
  userError: state.user.errors
})

const mapDispatchToProps = (dispatch) => ({
  getBusinessAccount: (businessAccountId) => dispatch(getBusinessAccount(businessAccountId)),
  getAutoRefundLogs: (settingsId) => dispatch(getAutoRefundLogs(settingsId)),
  updateBusinessAccount: (data) => dispatch(updateBusinessAccount(data)),
  userSaveResultReset: () => dispatch(userActions.userSaveResultReset()),
  getBusinessAccountsWithRole: () => dispatch(getBusinessAccountsWithRole()),
  resetError: () => dispatch(authErrorReset()),
  userErrorsReset: () => dispatch(userActions.userErrorsReset()),
  getPaymentProcessors: (data) => dispatch(getPaymentProcessors(data))
})

export default connect(mapStateToProps, mapDispatchToProps)(AutoRefundPage)
